/**
 * Copyright(c) 2022 Fenix Outdoor International AG.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Fenix ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Fenix.
 *
 */
import React from "react";
import PropTypes from "prop-types";
import Row from "../../../common/core/Row";
import Col from "../../../common/core/Col";
import Input from "../../../common/core/Input";
import Button from "../../../common/core/Button";
import Dropdown from "../../../common/core/UncontrolledDropdown";
import DropdownToggle from "../../../common/core/DropdownToggle";
import DropdownMenu from "../../../common/core/DropdownMenu";
import DropdownItem from "../../../common/core/DropdownItem";
import Pagination from "../../../common/components/Pagination";
import NoResult from "../../../common/components/NoResult";
import Tooltip from "../../../common/core/UncontrolledTooltip";
import Rules from "../../../common/security/PermissionRules";
import Permission from "../../../common/security/Permission";
import AddProdctBrandModal from "./AddProdctBrandModal";
import Alert from "../../../common/core/Alert";
import EditProductBrandModal from "./EditProductBrandModal";
import DeleteProductBrandModal from "./DeleteProductBrandModal";

const ProductBrandList = (props) => {
  const {
    brandList,
    totalBrandCount,
    availablePageSizes,
    selectedPage,
    selectedPageSize,
    searchTerm,
    isLoading,
    handleSearchTermChange,
    handleSearchButtonClick,
    handlePageChange,
    handlePageSizeChange,
    addModal,
    modalContents,
    toggleAddModal,
    handleChange,
    register,
    handleSubmit,
    submitAddBrandForm,
    errors,
    message,
    isSaveInProgress,
    toggleEditModal,
    editModal,
    submitEditBrandForm,
    handleDeleteConfirmationModal,
    deleteModal,
    handleDeleteProductBrand,
    deleteProductBrandInProgress,
  } = props;
  return (
    <>
      <Permission
        allowed={Rules.Control.VIEW_BRANDS_LIST}
      >
        <AddProdctBrandModal
          toggleAddModal={toggleAddModal}
          modalContents={modalContents}
          handleChange={handleChange}
          submitAddBrandForm={submitAddBrandForm}
          handleSubmit={handleSubmit}
          register={register}
          errors={errors}
          addModal={addModal}
          message={message}
          isSaveInProgress={isSaveInProgress}
        />
        <EditProductBrandModal
          toggleEditModal={toggleEditModal}
          modalContents={modalContents}
          handleChange={handleChange}
          submitEditBrandForm={submitEditBrandForm}
          handleSubmit={handleSubmit}
          register={register}
          errors={errors}
          editModal={editModal}
          message={message}
          isSaveInProgress={isSaveInProgress}
        />
        <DeleteProductBrandModal
          handleDeleteConfirmationModal={handleDeleteConfirmationModal}
          modalContents={modalContents}
          deleteModal={deleteModal}
          message={message}
          handleDeleteProductBrand={handleDeleteProductBrand}
          deleteProductBrandInProgress={deleteProductBrandInProgress}
        />
        <div className="contents">
          {message && message.type === "success" && (
            <Alert color={message.type} className="mt-2">
              {message.message}
            </Alert>
          )}
          {/* Search Box and Add new Buttons starts */}
          <h3 className="fw-bold mb-3">Product Brands</h3>
          <div className="search-outer mb-4">
            <Row>
              <Col md="7">
                <Permission
                  allowed={Rules.Control.SEARCH_BRAND}
                >
                  <Row className="g-0 align-items-center">
                    <Col className="pe-0 position-relative">
                      <Input
                        type="text"
                        size="lg"
                        value={searchTerm}
                        onChange={handleSearchTermChange}
                        placeholder="Search product brands"
                        onKeyPress={(e) => e.charCode === 13 && handleSearchButtonClick()}
                      />
                      <i className="search-icon"> </i>
                    </Col>
                    <Col xs="auto" className="ps-0">
                      <Button
                        color="primary"
                        className="btn-search"
                        size="lg"
                        onClick={handleSearchButtonClick}
                        disabled={isLoading}
                      >
                        Search
                      </Button>
                    </Col>
                    <Col xs="auto" className="ms-2">
                      <i className="fal fa-info-circle h5 text-danger" id="TooltipWildcard"> </i>
                      <Tooltip
                        placement="bottom"
                        target="TooltipWildcard"
                        className="tooltip-lg bs-tooltip-bottom"
                      >
                        <p className="text-start mb-3">
                          You can do wildcard searches also (case-insensitive):
                        </p>
                        <p className="text-start mb-3">
                          <Row className="g-2">
                            <Col xs="auto">
                              <i className="fal fa-share-all fw-bold"> </i>
                            </Col>
                            <Col>
                              AC* will show all product brands whose name starts with AC
                            </Col>
                          </Row>
                        </p>
                        <p className="text-start mb-3">
                          <Row className="g-2">
                            <Col xs="auto">
                              <i className="fal fa-share-all fw-bold"> </i>
                            </Col>
                            <Col>
                              *AC will show all product brands whose name ends with AC
                            </Col>
                          </Row>
                        </p>
                        <p className="text-start mb-0">
                          <Row className="g-2">
                            <Col xs="auto">
                              <i className="fal fa-share-all fw-bold"> </i>
                            </Col>
                            <Col>
                              *AC* will show all product  brands whose name contains AC anywhere
                            </Col>
                          </Row>
                        </p>
                      </Tooltip>
                    </Col>
                  </Row>
                </Permission>
              </Col>
              <Permission
                allowed={Rules.Control.ADD_BRAND}
              >
                <Col md="5" className="mt-3 mt-md-0 d-flex justify-content-end">
                  {/* TODO : Add add brand Functionality */}
                  <Button color="primary" size="lg" className="btn-addnew" onClick={toggleAddModal}>
                    <i className="fal fa-plus me-2"> </i>
                    Add New Product Brand
                  </Button>
                </Col>
              </Permission>
            </Row>
          </div>
          {/* Search Box and Add new Buttons ends */}
          {/* Page size selector starts */}
          {!isLoading && brandList && Array.isArray(brandList) && brandList.length > 0
            ? (
              <>
                <div className="data-outer">
                  <Row className="data-options mb-4">
                    <Col xs="12">
                      <div className="show-items">
                        Show
                        <Dropdown className="dropdown-list mx-2">
                          <DropdownToggle caret color="white">
                            {selectedPageSize}
                          </DropdownToggle>
                          <DropdownMenu right>
                            {availablePageSizes.map((pageSize) => (
                              <>
                                {pageSize && (pageSize === 20 || totalBrandCount >= pageSize) && (
                                  <DropdownItem
                                    active={pageSize === selectedPageSize}
                                    onClick={() => handlePageSizeChange(pageSize)}
                                  >
                                    {pageSize}
                                  </DropdownItem>
                                )}
                              </>
                            ))}
                          </DropdownMenu>
                        </Dropdown>
                        of
                        <span className="mx-1">{totalBrandCount}</span>
                        Results
                      </div>
                    </Col>
                  </Row>
                </div>
                {/* Page size selector ends */}
                {/* Brand Table Headers Starts */}
                <div className="data-list-outer">
                  <div className="data-header-outer">
                    <div className="seasons-list-header">
                      <Row>
                        <Col xs="4">Brand ID</Col>
                        <Col xs="4">Brand Name</Col>
                        <Col className="justify-content-end">Actions</Col>
                      </Row>
                    </div>
                  </div>
                  {/* Brand Table Headers Ends */}
                  {/* Brand Table Rows Starts */}
                  <ul className="season-list">
                    {brandList.map((branddata) => (
                      <>
                        <li className="season-list-item">
                          <Row>
                            <Col xs="4">{branddata.brandId}</Col>
                            <Col xs="4">{branddata.brandName}</Col>
                            <Col className="justify-content-end">
                              <Permission
                                allowed={Rules.Control.EDIT_BRAND}
                              >
                                <Button color="transparent" className="btn-edit" onClick={() => toggleEditModal(branddata)}>
                                  <i className="fal fa-pencil-alt"> </i>
                                </Button>
                              </Permission>
                              <Permission
                                allowed={Rules.Control.DELETE_BRAND}
                              >
                                <Button color="transparent" className="btn-delete" onClick={() => handleDeleteConfirmationModal(branddata)}>
                                  <i className="fal fa-trash-alt"> </i>
                                </Button>
                              </Permission>
                            </Col>
                          </Row>
                        </li>
                      </>
                    ))}
                  </ul>
                </div>
                {/* Brand Table Rows Ends */}
                <div className="pagination-outer">
                  <Pagination
                    selectedPage={selectedPage}
                    onPageChange={handlePageChange}
                    itemsPerPage={selectedPageSize}
                    totalCount={totalBrandCount}
                  />
                </div>
              </>
            ) : (
              <NoResult isLoading={isLoading} />
            )}
        </div>
      </Permission>
    </>
  );
};

ProductBrandList.defaultProps = {
  brandList: [],
  totalBrandCount: 0,
  availablePageSizes: [],
  searchTerm: "",
  selectedPage: 0,
  selectedPageSize: 20,
  isLoading: false,
  addModal: false,
  isSaveInProgress: false,
  deleteModal: false,
  deleteProductBrandInProgress: false,
};

ProductBrandList.propTypes = {
  brandList: PropTypes.arrayOf(PropTypes.any),
  totalBrandCount: PropTypes.number,
  availablePageSizes: PropTypes.arrayOf(PropTypes.any),
  searchTerm: PropTypes.string,
  selectedPage: PropTypes.number,
  selectedPageSize: PropTypes.number,
  isLoading: PropTypes.bool,
  addModal: PropTypes.bool,
  isSaveInProgress: PropTypes.bool,
  handleSearchTermChange: PropTypes.func.isRequired,
  handleSearchButtonClick: PropTypes.func.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  handlePageSizeChange: PropTypes.func.isRequired,
  toggleAddModal: PropTypes.func.isRequired,
  modalContents: PropTypes.objectOf(PropTypes.any).isRequired,
  handleChange: PropTypes.func.isRequired,
  submitAddBrandForm: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.objectOf(PropTypes.any).isRequired,
  message: PropTypes.objectOf(PropTypes.any).isRequired,
  toggleEditModal: PropTypes.func.isRequired,
  editModal: PropTypes.func.isRequired,
  submitEditBrandForm: PropTypes.func.isRequired,
  handleDeleteConfirmationModal: PropTypes.func.isRequired,
  deleteModal: PropTypes.bool,
  handleDeleteProductBrand: PropTypes.func.isRequired,
  deleteProductBrandInProgress: PropTypes.bool,
};

export default ProductBrandList;
